import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import './css/Inventory.css';

/** Must sit above `.menu-item-modal-overlay` (2100) and default inventory modals (1200). */
const DROPDOWN_Z_INDEX = 2200;
const PANEL_MAX_HEIGHT = 280;
const VIEWPORT_PAD = 8;

/**
 * Multi-select ingredients picker with search. Dropdown renders in a portal
 * so it is not clipped by the horizontal add-menu form scroll container.
 */
export default function IngredientsMultiSelect({
    options = [],
    selectedIds = [],
    onChange,
    placeholder = 'Select ingredients',
    searchPlaceholder = 'Search ingredients',
    loading = false,
    loadingLabel = 'Loading ingredients...',
    emptyMessage = 'No ingredients found',
    className = '',
    triggerClassName = 'add-menu-form-field inventory-select-trigger',
    dropdownMinWidth = 260,
}) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [panelStyle, setPanelStyle] = useState(null);
    const rootRef = useRef(null);
    const triggerRef = useRef(null);
    const panelRef = useRef(null);

    const normalizedSelectedIds = useMemo(
        () =>
            (Array.isArray(selectedIds) ? selectedIds : [])
                .map((id) => Number(id))
                .filter((id) => Number.isFinite(id)),
        [selectedIds]
    );

    const filteredOptions = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return options;
        return options.filter((option) =>
            String(option.ingredientName || '')
                .toLowerCase()
                .includes(q)
        );
    }, [options, search]);

    const updatePanelPosition = useCallback(() => {
        const el = triggerRef.current;
        if (!el) return;

        const rect = el.getBoundingClientRect();
        const gap = 6;
        const minW = Math.max(
            Math.round(rect.width),
            Number(dropdownMinWidth) > 0 ? Number(dropdownMinWidth) : 260
        );

        let left = rect.left;
        if (left + minW > window.innerWidth - VIEWPORT_PAD) {
            left = Math.max(VIEWPORT_PAD, window.innerWidth - minW - VIEWPORT_PAD);
        }

        const spaceBelow = window.innerHeight - rect.bottom - gap - VIEWPORT_PAD;
        const spaceAbove = rect.top - gap - VIEWPORT_PAD;
        const openAbove = spaceBelow < 180 && spaceAbove > spaceBelow;
        const available = openAbove ? spaceAbove : spaceBelow;
        const maxHeight = Math.max(160, Math.min(PANEL_MAX_HEIGHT, available));

        setPanelStyle({
            position: 'fixed',
            left,
            width: minW,
            zIndex: DROPDOWN_Z_INDEX,
            maxHeight,
            ...(openAbove
                ? { bottom: window.innerHeight - rect.top + gap }
                : { top: rect.bottom + gap }),
        });
    }, [dropdownMinWidth]);

    useLayoutEffect(() => {
        if (!open) {
            setPanelStyle(null);
            return undefined;
        }
        updatePanelPosition();
        const onScrollOrResize = () => updatePanelPosition();
        window.addEventListener('scroll', onScrollOrResize, true);
        window.addEventListener('resize', onScrollOrResize);
        return () => {
            window.removeEventListener('scroll', onScrollOrResize, true);
            window.removeEventListener('resize', onScrollOrResize);
        };
    }, [open, updatePanelPosition]);

    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = (e) => {
            const target = e.target;
            if (
                rootRef.current?.contains(target) ||
                panelRef.current?.contains(target)
            ) {
                return;
            }
            setOpen(false);
            setSearch('');
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [open]);

    const toggleOpen = () => {
        if (loading) return;
        setOpen((prev) => {
            if (prev) setSearch('');
            return !prev;
        });
    };

    const toggleIngredient = (ingredientId) => {
        const id = Number(ingredientId);
        if (!Number.isFinite(id)) return;
        const next = normalizedSelectedIds.includes(id)
            ? normalizedSelectedIds.filter((x) => x !== id)
            : [...normalizedSelectedIds, id];
        onChange(next);
    };

    const triggerLabel = loading
        ? loadingLabel
        : normalizedSelectedIds.length > 0
          ? `${normalizedSelectedIds.length} ingredient${normalizedSelectedIds.length === 1 ? '' : 's'} selected`
          : placeholder;

    const usesFieldTrigger =
        triggerClassName.includes('inventory-select-trigger') ||
        triggerClassName.includes('add-menu-form-field');

    const dropdownPanel = (
        <div
            ref={panelRef}
            className="ingredients-multi-select-panel"
            style={panelStyle ?? undefined}
            role="presentation"
            onMouseDown={(e) => e.stopPropagation()}
        >
            {loading ? (
                <div className="ingredients-loading-text">{loadingLabel}</div>
            ) : (
                <>
                    <div className="ingredients-multi-select-search">
                        <input
                            type="text"
                            className="ingredients-search-input"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={searchPlaceholder}
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                        />
                        {search.trim() !== '' && (
                            <button
                                type="button"
                                className="ingredients-search-clear"
                                aria-label="Clear ingredient search"
                                onClick={() => setSearch('')}
                            >
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        )}
                    </div>
                    <div className="ingredients-multi-select-options" role="listbox">
                        {filteredOptions.length === 0 ? (
                            <div className="inventory-option-empty">{emptyMessage}</div>
                        ) : (
                            filteredOptions.map((ingredient) => {
                                const id = Number(ingredient.ingredientId);
                                return (
                                    <label
                                        key={id || ingredient.ingredientName}
                                        className="ingredients-checkbox-row ingredients-multi-select-row"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={normalizedSelectedIds.includes(id)}
                                            onChange={() => toggleIngredient(id)}
                                        />
                                        <span>{ingredient.ingredientName}</span>
                                    </label>
                                );
                            })
                        )}
                    </div>
                </>
            )}
        </div>
    );

    return (
        <div
            className={`ingredients-multi-select${
                open ? ' ingredients-multi-select--open' : ''
            } ${className}`.trim()}
            ref={rootRef}
        >
            <button
                ref={triggerRef}
                type="button"
                className={`${triggerClassName}${
                    open
                        ? usesFieldTrigger
                            ? ' inventory-select-trigger--open'
                            : ' ingredients-dropdown-summary--open'
                        : ''
                }${!loading && normalizedSelectedIds.length === 0 ? ' inventory-select-trigger--placeholder' : ''}`.trim()}
                onClick={toggleOpen}
                disabled={loading}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                {triggerLabel}
            </button>
            {open &&
                panelStyle &&
                typeof document !== 'undefined' &&
                createPortal(dropdownPanel, document.body)}
        </div>
    );
}
